/**
 * Game store using Zustand.
 *
 * Manages local game state for the judge interface (TISSI).
 * Persists game state to localStorage for offline support and
 * recovery after page reload.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { LocalGameState, HalfScore, JudgeTeam } from '@/types';

/**
 * Create initial half score state.
 */
function createInitialHalf(): { ready: boolean; team1: HalfScore; team2: HalfScore } {
  return {
    ready: false,
    team1: { akka: 0, pappi: 0 },
    team2: { akka: 0, pappi: 0 },
  };
}

/**
 * Read current state directly from localStorage.
 * This bypasses Zustand's potentially stale in-memory state.
 */
function readFromLocalStorage(): { games: LocalGameState[]; finishedGames: LocalGameState[] } {
  try {
    const stored = localStorage.getItem('tissi-game-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        games: parsed.state?.games || [],
        finishedGames: parsed.state?.finishedGames || [],
      };
    }
  } catch {
    // Parse error, return defaults
  }
  return { games: [], finishedGames: [] };
}

/**
 * Write state directly to localStorage.
 */
function writeToLocalStorage(games: LocalGameState[], finishedGames: LocalGameState[]): void {
  try {
    localStorage.setItem('tissi-game-storage', JSON.stringify({
      state: { games, finishedGames },
      version: 0,
    }));
  } catch {
    // localStorage might be full or unavailable
  }
}

interface GameState {
  // State
  games: LocalGameState[];
  finishedGames: LocalGameState[];

  // Actions
  startGame: (id: string, paperId: number, round: number, league: string, team1: JudgeTeam, team2: JudgeTeam, userId?: number) => LocalGameState;
  getGame: (id: string) => LocalGameState | undefined;
  updateGame: (id: string, updates: Partial<LocalGameState>) => void;
  submitFirstHalf: (id: string, team1Score: HalfScore, team2Score: HalfScore) => void;
  submitSecondHalf: (id: string, team1Score: HalfScore, team2Score: HalfScore) => void;
  submitOvertime: (id: string, team1Score: HalfScore, team2Score: HalfScore) => void;
  editHalf: (id: string, half: 'firstHalf' | 'secondHalf' | 'overtime') => void;
  endGame: (id: string) => void;
  cancelGame: (id: string) => void;
  reopenGame: (id: string) => LocalGameState | undefined;
  clearFinishedGames: () => void;
  clearAllGames: () => void;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Initial state
      games: [],
      finishedGames: [],

      // Start a new game (returns existing if already exists)
      startGame: (id, paperId, round, league, team1, team2, userId) => {
        // Read directly from localStorage to avoid stale Zustand state
        const { games, finishedGames } = readFromLocalStorage();
        
        const existing = games.find((g) => g.id === id);
        if (existing) {
          // Sync Zustand state with localStorage
          set({ games, finishedGames });
          return existing;
        }

        const newGame: LocalGameState = {
          id,
          paperId,
          round,
          league,
          team1,
          team2,
          startTime: new Date().toISOString(),
          userId,
          firstHalf: createInitialHalf(),
          secondHalf: createInitialHalf(),
          overtime: createInitialHalf(),
          finished: false,
          cancelled: false,
        };

        const newGames = [...games, newGame];
        
        // Write to localStorage FIRST
        writeToLocalStorage(newGames, finishedGames);
        
        // Then update Zustand state
        set({ games: newGames, finishedGames });
        
        return newGame;
      },

      // Get a game by ID
      getGame: (id) => {
        return get().games.find((g) => g.id === id);
      },

      // Update a game's state
      updateGame: (id, updates) => {
        set((state) => ({
          games: state.games.map((g) => (g.id === id ? { ...g, ...updates } : g)),
        }));
      },

      // Submit first half scores
      submitFirstHalf: (id, team1Score, team2Score) => {
        set((state) => ({
          games: state.games.map((g) =>
            g.id === id
              ? { ...g, firstHalf: { ready: true, team1: team1Score, team2: team2Score } }
              : g
          ),
        }));
      },

      // Submit second half scores
      submitSecondHalf: (id, team1Score, team2Score) => {
        set((state) => ({
          games: state.games.map((g) =>
            g.id === id
              ? { ...g, secondHalf: { ready: true, team1: team1Score, team2: team2Score } }
              : g
          ),
        }));
      },

      // Submit overtime scores
      submitOvertime: (id, team1Score, team2Score) => {
        set((state) => ({
          games: state.games.map((g) =>
            g.id === id
              ? { ...g, overtime: { ready: true, team1: team1Score, team2: team2Score } }
              : g
          ),
        }));
      },

      // Edit a half (reset ready state)
      editHalf: (id, half) => {
        set((state) => ({
          games: state.games.map((g) =>
            g.id === id ? { ...g, [half]: { ...g[half], ready: false } } : g
          ),
        }));
      },

      // End a game (move to finished)
      endGame: (id) => {
        // Read directly from localStorage to avoid stale state
        const { games, finishedGames } = readFromLocalStorage();
        const game = games.find((g) => g.id === id);
        if (!game) return;

        const finishedGame = { ...game, finished: true };
        const newGames = games.filter((g) => g.id !== id);
        const newFinishedGames = [finishedGame, ...finishedGames];

        // Write to localStorage FIRST
        writeToLocalStorage(newGames, newFinishedGames);
        
        // Then update Zustand state
        set({ games: newGames, finishedGames: newFinishedGames });
      },

      // Cancel a game
      cancelGame: (id) => {
        // Read directly from localStorage to avoid stale state
        const { games, finishedGames } = readFromLocalStorage();
        const newGames = games.filter((g) => g.id !== id);
        
        // Write to localStorage FIRST
        writeToLocalStorage(newGames, finishedGames);
        
        // Then update Zustand state
        set({ games: newGames, finishedGames });
      },

      // Reopen a finished game (move from finishedGames back to games)
      reopenGame: (id) => {
        // Read directly from localStorage to avoid stale state
        const { games, finishedGames } = readFromLocalStorage();
        const game = finishedGames.find((g) => g.id === id);
        if (!game) return undefined;

        // Mark game as not finished
        const reopenedGame = { ...game, finished: false };
        const newFinishedGames = finishedGames.filter((g) => g.id !== id);
        const newGames = [...games, reopenedGame];

        // Write to localStorage FIRST
        writeToLocalStorage(newGames, newFinishedGames);

        // Then update Zustand state
        set({ games: newGames, finishedGames: newFinishedGames });

        return reopenedGame;
      },

      // Clear all finished games
      clearFinishedGames: () => {
        // Read directly from localStorage to avoid stale state
        const { games } = readFromLocalStorage();
        
        // Write to localStorage FIRST
        writeToLocalStorage(games, []);
        
        // Then update Zustand state
        set({ games, finishedGames: [] });
      },

      // Clear all games (both active and finished) - used after tournament regeneration
      clearAllGames: () => {
        // Write to localStorage FIRST
        writeToLocalStorage([], []);
        
        // Then update Zustand state
        set({ games: [], finishedGames: [] });
      },
    }),
    {
      name: 'tissi-game-storage',
      // Persist all state
      partialize: (state) => ({
        games: state.games,
        finishedGames: state.finishedGames,
      }),
    }
  )
);

// Re-export the hook type for backwards compatibility
export type UseGameStateReturn = {
  games: LocalGameState[];
  finishedGames: LocalGameState[];
  startGame: (id: string, paperId: number, round: number, league: string, team1: JudgeTeam, team2: JudgeTeam, userId?: number) => LocalGameState;
  getGame: (id: string) => LocalGameState | undefined;
  updateGame: (id: string, updates: Partial<LocalGameState>) => void;
  submitFirstHalf: (id: string, team1Score: HalfScore, team2Score: HalfScore) => void;
  submitSecondHalf: (id: string, team1Score: HalfScore, team2Score: HalfScore) => void;
  submitOvertime: (id: string, team1Score: HalfScore, team2Score: HalfScore) => void;
  editHalf: (id: string, half: 'firstHalf' | 'secondHalf' | 'overtime') => void;
  endGame: (id: string) => void;
  cancelGame: (id: string) => void;
  reopenGame: (id: string) => LocalGameState | undefined;
  clearFinishedGames: () => void;
  clearAllGames: () => void;
};

/**
 * Backwards-compatible hook that wraps the Zustand store.
 * This allows existing code to continue using `useGameState()` without changes.
 */
export function useGameState(): UseGameStateReturn {
  return useGameStore();
}

/**
 * Clear all judge games from localStorage.
 * Used when tournament is regenerated to avoid stale game references.
 */
export function clearJudgeGamesStorage(): void {
  useGameStore.getState().clearAllGames();
}

